import request from '@/config/axios'

/** 资源类型：company 企业 / assoc 商会 */
export type ResourceKind = 'company' | 'assoc'

/** 归属状态：claimed 已认领 / claimable 待认领 */
export type ResourceClaimStatus = 'claimed' | 'claimable'

/** 审核状态：pending 待审核 / approved 已通过 / rejected 已驳回 */
export type ResourceAuditStatus = 'pending' | 'approved' | 'rejected'

export interface ResourceVO {
  id?: string
  kind?: ResourceKind
  name?: string
  shortName?: string
  logo?: string
  logoBg?: string
  industry?: string
  scale?: string
  city?: string
  district?: string
  founded?: string
  creditCode?: string
  legalPerson?: string
  capital?: string
  claimStatus?: ResourceClaimStatus
  verified?: boolean
  intro?: string
  score?: number
  viewCount?: number
  memberCount?: number
  activitiesMonth?: number
  phone?: string
  phoneMasked?: string
  address?: string
  website?: string
  scope?: string
  followCount?: number
  favoriteCount?: number
  matchScore?: number
  matchReason?: string
  ownerUserId?: string
  source?: string
  /** 0 正常 1 下架 */
  status?: number
  sort?: number
  createTime?: string
}

export interface ResourceClaimVO {
  id?: string
  resourceId?: string
  resourceName?: string
  userId?: string
  claimNo?: string
  /** 认领人职务 */
  role?: string
  license?: string
  idProof?: string
  authProof?: string
  mobile?: string
  status?: ResourceAuditStatus
  rejectReason?: string
  auditTime?: string
  createTime?: string
}

export interface ResourceApplyVO {
  id?: string
  userId?: string
  name?: string
  shortName?: string
  creditCode?: string
  industry?: string
  city?: string
  intro?: string
  license?: string
  photo?: string
  contact?: string
  mobile?: string
  status?: ResourceAuditStatus
  rejectReason?: string
  resourceId?: string
  auditTime?: string
  createTime?: string
}

/** 审核入参：approved=true 通过；false 驳回（驳回时 rejectReason 必填） */
export interface ResourceAuditReqVO {
  id: string
  approved: boolean
  rejectReason?: string
}

/** 法定代表人候选：取自系统会员，选中后手机号自动带入「联系电话」 */
export interface ResourceUserCandidateVO {
  userId?: string
  /** 展示名：优先真实姓名，未登记真名时退回昵称 */
  name?: string
  nickname?: string
  mobile?: string
}

// ==================== 资源列表 ====================
export const getResourcePage = (params: {
  pageNo: number
  pageSize: number
  kind?: ResourceKind
  keyword?: string
  city?: string
  claimStatus?: ResourceClaimStatus
  status?: number
}) => request.get({ url: '/bosscircle/resource/page', params })

export const getResource = (id: string | number) =>
  request.get({ url: '/bosscircle/resource/get', params: { id } })

export const createResource = (data: Partial<ResourceVO> & { tags?: string[]; bizTags?: string[] }) =>
  request.post({ url: '/bosscircle/resource/create', data })

export const updateResource = (data: Partial<ResourceVO> & { tags?: string[]; bizTags?: string[] }) =>
  request.put({ url: '/bosscircle/resource/update', data })

export const deleteResource = (id: string | number) =>
  request.delete({ url: '/bosscircle/resource/delete', params: { id } })

/** 法定代表人候选：按姓名/昵称模糊匹配系统会员，整串手机号时另走精确查号 */
export const getResourceUserCandidates = (keyword: string) =>
  request.get({ url: '/bosscircle/resource/legal-person-lookup', params: { keyword } })

// ==================== 认领审核 ====================
export const getResourceClaimPage = (params: {
  pageNo: number
  pageSize: number
  status?: ResourceAuditStatus
}) => request.get({ url: '/bosscircle/resource/claim/page', params })

export const auditResourceClaim = (data: ResourceAuditReqVO) =>
  request.put({ url: '/bosscircle/resource/claim/audit', data })

// ==================== 入驻审核 ====================
export const getResourceApplyPage = (params: {
  pageNo: number
  pageSize: number
  status?: ResourceAuditStatus
}) => request.get({ url: '/bosscircle/resource/apply/page', params })

export const auditResourceApply = (data: ResourceAuditReqVO) =>
  request.put({ url: '/bosscircle/resource/apply/audit', data })

// ==================== 协会入会审核 ====================
/** 入会状态：pending 待审核 / joined 已入会 / rejected 已驳回（与认领、入驻的 approved 用词不同） */
export type ResourceAssocJoinStatus = 'pending' | 'joined' | 'rejected'

export interface ResourceAssocJoinVO {
  id?: string
  resourceId?: string
  resourceName?: string
  userId?: string
  userNickname?: string
  userAvatar?: string
  status?: ResourceAssocJoinStatus
  rejectReason?: string
  auditUserId?: string
  auditTime?: string
  createTime?: string
}

export const getResourceAssocJoinPage = (params: {
  pageNo: number
  pageSize: number
  status?: ResourceAssocJoinStatus
  resourceId?: string
}) => request.get({ url: '/bosscircle/resource/assoc/join/page', params })

export const auditResourceAssocJoin = (data: ResourceAuditReqVO) =>
  request.put({ url: '/bosscircle/resource/assoc/join/audit', data })

// ==================== 资料纠错 ====================
/** 纠错状态：pending 待处理 / adopted 已采纳 / rejected 不采纳 */
export type ResourceCorrectionStatus = 'pending' | 'adopted' | 'rejected'

export interface ResourceCorrectionVO {
  id?: string
  resourceId?: string
  resourceName?: string
  userId?: string
  userNickname?: string
  userAvatar?: string
  infoField?: string
  wrongValue?: string
  rightValue?: string
  content?: string
  images?: string[]
  status?: ResourceCorrectionStatus
  handleRemark?: string
  handleUserId?: string
  handleTime?: string
  createTime?: string
}

export const getResourceCorrectionPage = (params: {
  pageNo: number
  pageSize: number
  status?: ResourceCorrectionStatus
  resourceId?: string
  keyword?: string
}) => request.get({ url: '/bosscircle/resource/correction/page', params })

export const handleResourceCorrection = (data: {
  id: string
  adopted: boolean
  handleRemark?: string
}) => request.put({ url: '/bosscircle/resource/correction/handle', data })

// ==================== 商机对接申请 ====================
/** 对接状态：pending 待处理 / approved 已通过 / rejected 未通过 */
export type ResourceContactStatus = 'pending' | 'approved' | 'rejected'

export interface ResourceContactVO {
  id?: string
  resourceId?: string
  resourceName?: string
  bizOppId?: string
  bizOppTitle?: string
  userId?: string
  userNickname?: string
  userAvatar?: string
  purpose?: string
  contactMobile?: string
  status?: ResourceContactStatus
  rejectReason?: string
  handleUserId?: string
  handleTime?: string
  createTime?: string
}

export const getResourceContactPage = (params: {
  pageNo: number
  pageSize: number
  status?: ResourceContactStatus
  resourceId?: string
  keyword?: string
}) => request.get({ url: '/bosscircle/resource/contact/page', params })

export const auditResourceContact = (data: ResourceAuditReqVO) =>
  request.put({ url: '/bosscircle/resource/contact/audit', data })